import React, { useEffect, useState } from "react";
import { Check, Loader2, ShieldCheck, AlertTriangle, RefreshCw, MessageCircle } from "lucide-react";

const SLOW_AFTER_MS = 8000;
const SUPPORT_WHATSAPP = "918141817353";

const STEPS = [
  { key: "paid", label: "Payment received" },
  { key: "verifying", label: "Verifying payment" },
  { key: "activating", label: "Activating your plan" },
];

const STAGE_ORDER = { verifying: 1, activating: 2 };

const formatAmount = (amount) =>
  Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const StepIcon = ({ state }) => {
  if (state === "done") {
    return (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
        <Check size={15} strokeWidth={3} />
      </span>
    );
  }
  if (state === "active") {
    return (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#503691]/10 text-[#503691]">
        <Loader2 size={16} className="animate-spin motion-reduce:animate-none" />
      </span>
    );
  }
  return <span className="h-7 w-7 shrink-0 rounded-full border-2 border-slate-200 bg-white" />;
};

/**
 * Shown from the moment Razorpay reports a successful payment until the plan
 * is confirmed by the server, so the user never sees a silent page while
 * /payment/verify and /plans/activate run.
 *
 * progress: { stage: "verifying" | "activating" | "error", amount, paymentId,
 *             planName, message, planMayBeActive }
 */
const PaymentProcessingOverlay = ({ progress, onRetry, onClose }) => {
  const [isSlow, setIsSlow] = useState(false);
  const stage = progress?.stage;
  const isWorking = stage === "verifying" || stage === "activating";

  useEffect(() => {
    setIsSlow(false);
    if (!isWorking) return undefined;
    const timer = window.setTimeout(() => setIsSlow(true), SLOW_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [stage, isWorking]);

  // A refresh or tab close mid-activation is what leaves people unsure whether
  // they paid; ask the browser to confirm first.
  useEffect(() => {
    if (!isWorking) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isWorking]);

  if (!progress) return null;

  const supportUrl = `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(
    `Hi, my payment for ${progress.planName || "a PRObroker plan"} was successful but the plan is not showing as active. Payment ID: ${progress.paymentId || "-"}`
  )}`;

  return (
    <div
      className="fixed inset-0 z-[230] flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-processing-title"
    >
      <div className="w-full max-w-md overflow-hidden rounded-[1.5rem] bg-white shadow-[0_30px_120px_rgba(15,23,42,0.38)]">
        <div className={`h-1.5 ${stage === "error" ? "bg-amber-400" : "bg-gradient-to-r from-[#503691] via-violet-500 to-emerald-400"}`} />

        {stage === "error" ? (
          <div className="px-5 py-6 sm:px-7">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-600">
              <AlertTriangle size={28} />
            </div>
            <h2 id="payment-processing-title" className="mt-4 text-center text-xl font-black text-slate-900">
              {progress.planMayBeActive ? "Almost done" : "We're still confirming your plan"}
            </h2>
            <p className="mt-2 text-center text-sm leading-relaxed text-slate-600">
              Your payment of <strong className="text-slate-900">₹{formatAmount(progress.amount)}</strong> was received.
              {progress.planMayBeActive
                ? " Your plan is active, but we couldn't finish saving your billing details."
                : " We couldn't confirm the activation yet."}
            </p>

            <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              <ShieldCheck size={18} className="mt-0.5 shrink-0 text-emerald-600" />
              <p>
                <strong>Please don't pay again.</strong> Your money is safe. Trying again uses the same payment and won't
                charge you twice.
              </p>
            </div>

            {progress.paymentId && (
              <p className="mt-3 text-center text-xs text-slate-500">
                Payment ID: <span className="font-mono font-semibold text-slate-700">{progress.paymentId}</span>
              </p>
            )}
            {progress.message && (
              <p className="mt-1 text-center text-xs text-slate-400">{progress.message}</p>
            )}

            <div className="mt-5 flex flex-col gap-2.5">
              <button
                type="button"
                onClick={onRetry}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#503691] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-[#503691]/20 transition-colors hover:bg-[#503691]/85"
              >
                <RefreshCw size={16} />
                Try again
              </button>
              <a
                href={supportUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-5 py-3 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50"
              >
                <MessageCircle size={16} />
                Contact support on WhatsApp
              </a>
              <button
                type="button"
                onClick={onClose}
                className="py-1.5 text-xs font-semibold text-slate-500 underline underline-offset-2 hover:text-slate-700"
              >
                Close and check My Plans later
              </button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-7 sm:px-7">
            <div className="relative mx-auto h-16 w-16">
              <span className="absolute inset-0 rounded-full border-4 border-[#503691]/15" />
              <span className="absolute inset-0 animate-spin rounded-full border-4 border-transparent border-t-[#503691] motion-reduce:animate-none" />
              <span className="absolute inset-0 flex items-center justify-center text-[#503691]">
                <ShieldCheck size={26} />
              </span>
            </div>

            <h2 id="payment-processing-title" className="mt-4 text-center text-xl font-black text-slate-900">
              Confirming your payment
            </h2>
            <p className="mt-1.5 text-center text-sm text-slate-500">
              ₹{formatAmount(progress.amount)} paid{progress.planName ? ` for ${progress.planName}` : ""}. Setting up your
              plan now.
            </p>

            <ol className="mt-5 space-y-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
              {STEPS.map((step, index) => {
                const current = STAGE_ORDER[stage] ?? 0;
                const state = index < current ? "done" : index === current ? "active" : "pending";
                return (
                  <li key={step.key} className="flex items-center gap-3">
                    <StepIcon state={state} />
                    <span
                      className={`text-sm font-semibold ${
                        state === "pending" ? "text-slate-400" : state === "active" ? "text-[#503691]" : "text-slate-800"
                      }`}
                    >
                      {step.label}
                      {state === "active" ? "…" : ""}
                    </span>
                  </li>
                );
              })}
            </ol>

            <p className="mt-4 text-center text-xs font-medium text-slate-500" aria-live="polite">
              {isSlow
                ? "This is taking a little longer than usual. Still working, please stay on this page."
                : "Please don't close or refresh this page. This usually takes a few seconds."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentProcessingOverlay;
